import axios from "axios";
import { ACCESS_TOKEN } from "../constants/BrowserStorage";

/**
 * DVS-1535: attach the member bearer token to the admin reporting API requests.
 *
 * The reporting pages call the admin API (`.../webapi/adminreporting/...`) with the global
 * `axios` instance directly, and most of them send no auth header today. This global request
 * interceptor adds the bearer token (from localStorage) so those calls keep working once the
 * server requires admin auth.
 *
 * The token is scoped to the admin API on the service origin only — matched on the fully-resolved
 * request URL (baseURL + url), by origin AND path (the admin route itself or a `/` boundary after
 * it) — so the member JWT is never sent to another host or a path that merely starts with the segment.
 *
 * Imported once for side effects from index.tsx.
 */
const SERVICE_URL = process.env.REACT_APP_NOT_SECRET_SERVICE_URL ?? "";
const ADMIN_API_PATH = "/webapi/adminreporting";

const targetsAdminApi = (baseURL: string | undefined, url: string | undefined): boolean => {
  try {
    const fallbackBase =
      SERVICE_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const resolved = new URL(url ?? "", `${baseURL ?? ""}` || fallbackBase);
    const serviceOrigin = SERVICE_URL
      ? new URL(SERVICE_URL).origin
      : typeof window !== "undefined"
      ? window.location.origin
      : "";
    return (
      resolved.origin === serviceOrigin &&
      (resolved.pathname === ADMIN_API_PATH ||
        resolved.pathname.startsWith(`${ADMIN_API_PATH}/`))
    );
  } catch {
    return false;
  }
};

axios.interceptors.request.use((config) => {
  if (targetsAdminApi(config.baseURL, config.url)) {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (token) {
      config.headers = config.headers ?? {};
      // The stored value is loginmember's `bearerToken`, which already includes the "Bearer " scheme.
      (config.headers as Record<string, string>).Authorization = token;
    }
  }
  return config;
});

// A stored token the admin API rejects (401, or 403 for a token issued before DVS-1535 without
// the cm_authenticated claim) never recovers, and the app has no logout, so drop it and go to the
// root, where ProtectedRoutes sends the user to /login. The token check stops a second redirect
// when several calls fail at once.
axios.interceptors.response.use(undefined, (error) => {
  const status = error?.response?.status;
  if (
    (status === 401 || status === 403) &&
    targetsAdminApi(error?.config?.baseURL, error?.config?.url) &&
    localStorage.getItem(ACCESS_TOKEN)
  ) {
    localStorage.removeItem(ACCESS_TOKEN);
    window.location.replace("/");
  }
  return Promise.reject(error);
});

export {};
